// lib
import React, { useReducer, useState, useRef, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { isEqual, truncate } from 'lodash'

import { plural } from '@aytm/app/stats/lib/helpers'
import SchemaRules from '@aytm/app/stats/lib/schema-rules'
import BasicFilters from '@aytm/app/stats/lib/basic-filters'

import containerExpander from '@aytm/lib/layout/container-expander'

import useNonInitialEffect from '@aytm/app/stats/hooks/useNonInitialEffect'
import useEventListener from '@aytm/app/stats/hooks/useEventListener'

import { usePrevious } from '@aytm/app/insights/dashboard/components/hooks'
import { filtersReducer } from '@aytm/app/stats/page/sidebar/filters/reducers'
import {
  changeBasicValue,
  deleteBasicValue,
  deleteCascadeBasicValue,
  setAdvancedValue,
  setBasicValue,
  switchMode,
  updateCascadeBasicValue
} from '@aytm/app/stats/page/sidebar/filters/actions'

import { getLogicData } from '@aytm/app/stats/lib/refs'
import { convertValuesToTree, convertTreeToValues } from '@aytm/app/stats/lib/converter'

// components
import { Button } from 'primereact/button'

import TextPopup from '@aytm/lib/rfd-ui/components/basic/text-popup'

import FiltersAdvanced from '@aytm/app/stats/page/sidebar/filters/FiltersAdvanced'
import FiltersUids from '@aytm/app/stats/page/sidebar/filters/FiltersUids'
import FiltersGroup from '@aytm/app/stats/page/sidebar/filters/FiltersGroup'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

export const DataContext = React.createContext()

const FiltersPanel = ({
  root,
  data,
  features,
  parser,
  locale,
  filtersMode = 'basic',
  filtersPattern = '',
  externalData = [],
  filters,
  tags,
  codeBooks,
  clusters,
  surveySchema,
  disabled,
  isAdmin,
  uids = [],
  uidsTruncated = false,
  onChange,
  onScrollToQuestion,
  onSwitchMode,
  onShowHiddenGroup,
  onLoad,
  onDNDStart,
  onDNDEnd,
  restoreQuestion,
  getApiKey
}) => {
  const clustersPrev = usePrevious(clusters)
  const surveySchemaPrev = usePrevious(surveySchema)

  const [state, dispatch] = useReducer(filtersReducer, {
    mode: filtersMode,
    basic: null,
    advanced: filtersPattern
  })

  const [filtersApplying, setFiltersApplying] = useState(false)
  const [filtersReleasing, setFiltersReleasing] = useState(false)
  const [filtersCountApplied, setFiltersCountApplied] = useState(null)

  const questionsCallback = useRef(null)
  const dropdownCallback = useRef(null)

  const componentRef = useRef()
  const dropdownsRef = useRef({})

  const overlayRef = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const checkBasicFilters = useRef(true)
  checkBasicFilters.current =
    surveySchemaPrev && clustersPrev
      ? !isEqual(surveySchemaPrev.tags, surveySchema.tags) ||
        !isEqual(surveySchemaPrev.sentiments, surveySchema.sentiments) ||
        !isEqual(surveySchemaPrev.codeBooks, surveySchema.codeBooks) ||
        !isEqual(surveySchemaPrev.customVariables, surveySchema.customVariables)
      : true

  const basicFilters = useMemo(
    () =>
      new BasicFilters({
        clusters: clusters,
        surveySchema: surveySchema,
        deepAllowed: true,
        tagsAllowed: true,
        sentimentsAllowed: true,
        isAdmin
      }),
    [surveySchema, clusters, isAdmin]
  )

  const basicFiltersRef = useRef()
  basicFiltersRef.current = basicFilters

  const filtersChangingRef = useRef(false)
  const filtersBasicChangingRef = useRef(false)

  const contentContainerExpanderRef = useRef()

  const parserResultEof = useMemo(
    () =>
      parser.analyzeString({
        filter: '',
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surveySchema]
  )
  const parserResultPattern = useMemo(
    () =>
      parser.analyzeString({
        filter: state.advanced.trim(),
        clusters: clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.advanced, surveySchema, clusters]
  )
  const parserResultPatternPrev = usePrevious(parserResultPattern)

  const parserResultPatternError = useMemo(
    () =>
      0 < stateRef.current.advanced.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  const parserResultPatternLogicData = useMemo(
    () => getLogicData(tags, codeBooks, parserResultPattern, parserResultPatternError),
    [tags, codeBooks, parserResultPattern, parserResultPatternError]
  )

  const filtersUIDs = useMemo(() => {
    const questions = filters.find(group => 'questions' == group.key)
    if (questions == undefined) return {}

    return null === parserResultPatternBasic
      ? {}
      : Object.keys(parserResultPatternBasic).reduce((acc, cur) => {
          const question = questions.values.find(q => cur == q.key)

          if (question != undefined && question.uid != undefined) {
            acc[question.key] = question.uid
          } else if (question != undefined && question.options.some(option => option.uid != undefined)) {
            acc[cur] = question.options.reduce((_acc, _cur) => {
              _acc[_cur.value] = _cur.uid
              return _acc
            }, {})
          }

          return acc
        }, {})
  }, [filters, parserResultPatternBasic])

  const filtersChanged = useMemo(() => state.advanced != filtersPattern, [state.advanced, filtersPattern])

  const scrollAdjustment = useCallback(
    overlay => {
      const rootRect = root.getBoundingClientRect()
      const overlayRect = overlay.getBoundingClientRect()
      if (rootRect.top > overlayRect.top || rootRect.bottom < overlayRect.bottom) {
        overlay.scrollIntoView(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [root]
  )

  const scrollCallback = useCallback(
    key => {
      const dropdownNode = dropdownsRef.current[key].current
      const overlay = dropdownNode.getOverlay()
      if (null === overlay) {
        setTimeout(() => dropdownNode.show(), 10)
      } else {
        scrollAdjustment(overlay)
      }
    },
    [scrollAdjustment]
  )

  const onApplyFilters = useCallback(() => {
    setFiltersApplying(true)

    onChange({
      filtersCount: null !== parserResultPatternBasic ? Object.keys(parserResultPatternBasic).length : 0,
      filtersPattern: state.advanced,
      filtersMode: state.mode,
      filtersModeDisabled: null === parserResultPatternBasic,
      logicData: parserResultPatternLogicData
    })
  }, [state.advanced, state.mode, parserResultPatternBasic, parserResultPatternLogicData, onChange])

  const onReleaseActiveFilters = useCallback(() => {
    setFiltersReleasing(true)

    onChange({
      filtersCount: 0,
      filtersPattern: '',
      filtersMode: state.mode,
      filtersModeDisabled: false,
      logicData: []
    })
  }, [state.mode, onChange])

  // ONCHANGE FILTER HANDLERS
  const onChangeBasicFilter = useCallback(
    (filter, _values) => {
      const values =
        null === _values ||
        ('sliderselect' == filter.type &&
          (_values[0] < 1 ||
            _values[1] > filter.options.length ||
            (1 == _values[0] && _values[1] == filter.options.length)))
          ? 'sliderselect' == filter.type && !(1 == _values[0] && _values[1] == filter.options.length)
            ? [1, filter.options.length]
            : []
          : _values
      const treeValue = convertValuesToTree(filter, filter.key, values, 'value')
      if ('dropdown' == filter.type && filter.subkey != undefined) {
        updateCascadeBasicValue(dispatch, filter.key.toUpperCase(), filter.subkey.toUpperCase(), treeValue)
      } else if ('button' == filter.type && state.basic[filter.key.toUpperCase()]) {
        deleteBasicValue(dispatch, filter.key)
      } else {
        if (0 < Object.keys(treeValue[filter.key.toUpperCase()]).length) {
          changeBasicValue(dispatch, treeValue)
        } else deleteBasicValue(dispatch, filter.key)
      }
    },
    [state.basic, dispatch]
  )
  const onResetBasicFilter = useCallback(
    filter => {
      if ('dropdown' == filter.type && filter.subkey != undefined) {
        deleteCascadeBasicValue(dispatch, filter.key.toUpperCase(), filter.subkey.toUpperCase())
      } else {
        deleteBasicValue(dispatch, filter.key)
      }
    },
    [dispatch]
  )
  const onChangeAdvancedFilter = useCallback((value = '') => setAdvancedValue(dispatch, value), [dispatch])

  const onShowFilterDropdown = useCallback(
    dropdownNode => {
      const overlay = dropdownNode.getOverlay()
      contentContainerExpanderRef.current.expand(overlay)
      setTimeout(() => {
        scrollAdjustment(overlay)
      }, 50)

      overlayRef.current = overlay
    },
    [scrollAdjustment]
  )

  const onHideFilterDropdown = useCallback(
    dropdownNode => {
      const overlay = dropdownNode.getOverlay()
      contentContainerExpanderRef.current.unexpand(overlay)
      setTimeout(() => {
        scrollAdjustment(overlay)
      }, 50)

      overlayRef.current = null
    },
    [scrollAdjustment]
  )

  useEffect(() => {
    if ('function' == typeof questionsCallback.current) {
      questionsCallback.current()
    }
    questionsCallback.current = null
  }, [filters])

  useEffect(() => {
    if (stateRef.current.advanced != filtersPattern) {
      setAdvancedValue(dispatch, filtersPattern)
    }
    setFiltersApplying(false)
    setFiltersReleasing(false)
  }, [filtersPattern])

  useEffect(() => {
    if (stateRef.current.mode != filtersMode) {
      switchMode(dispatch, filtersMode)
    }
  }, [filtersMode])

  useEffect(() => {
    if (state.advanced == filtersPattern) {
      const filtersCount = null !== parserResultPatternBasic ? Object.keys(parserResultPatternBasic).length : 0
      setFiltersCountApplied(filtersCount)

      onLoad({
        filtersCount,
        filtersMode: stateRef.current.mode,
        filtersModeDisabled: null === parserResultPatternBasic
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.advanced, filtersPattern, parserResultPatternBasic, onLoad])

  useEffect(() => {
    if (null !== dropdownCallback.current && dropdownsRef.current[dropdownCallback.current] != undefined) {
      scrollCallback(dropdownCallback.current)
      dropdownCallback.current = null
    }

    if (filtersChangingRef.current) {
      filtersChangingRef.current = false
    } else if (state.basic && !isEqual(state.basic, parserResultPatternBasic)) {
      filtersBasicChangingRef.current = true

      setAdvancedValue(dispatch, basicFiltersRef.current.convertToAdvanced(state.basic))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.basic, parserResultPatternBasic])

  useEffect(() => {
    onSwitchMode({
      filtersCount: filtersCountApplied,
      filtersMode:
        ((parserResultPatternError || null === parserResultPatternBasic) &&
          0 < stateRef.current.advanced.length &&
          'advanced') ||
        state.mode
    })

    root.scroll({
      top: 0,
      left: 0
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.mode, onSwitchMode])

  useEffect(() => {
    if (filtersBasicChangingRef.current) {
      filtersBasicChangingRef.current = false

      setParserResultPatternBasic(stateRef.current.basic)

      return
    }

    try {
      const tree = parserResultPatternError
        ? null
        : 0 < stateRef.current.advanced.trim().length
          ? 0 < parserResultPattern.expression.status.errors.length
            ? null
            : parserResultPattern &&
                parserResultPatternPrev &&
                isEqual(parserResultPattern.expression.status, parserResultPatternPrev.expression.status) &&
                !checkBasicFilters.current
              ? parserResultPatternBasic
              : basicFiltersRef.current.convertToBasic(SchemaRules.flatten(parserResultPattern.expression.tree)) || null
          : {}

      filtersChangingRef.current = tree !== parserResultPatternBasic

      setParserResultPatternBasic(tree)
    } catch {
      filtersChangingRef.current = null !== parserResultPatternBasic

      setParserResultPatternBasic(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPattern, parserResultPatternError])

  useEffect(
    () => (contentContainerExpanderRef.current = containerExpander(componentRef.current, { margin: 0 })),
    [componentRef]
  )

  useNonInitialEffect(() => {
    if (null == parserResultPatternBasic) {
      if ('basic' == stateRef.current.mode) {
        root.scroll({
          top: 0,
          left: 0
        })
      }

      filtersChangingRef.current = true

      switchMode(dispatch, 'advanced')
      setBasicValue(dispatch, null)
    } else {
      if (!isEqual(stateRef.current.basic, parserResultPatternBasic)) {
        filtersChangingRef.current = true

        setBasicValue(dispatch, parserResultPatternBasic)
      }
      if (
        'advanced' == stateRef.current.mode &&
        !stateRef.current.advanced.trim().length &&
        features.advanced.disabled
      ) {
        root.scroll({
          top: 0,
          left: 0
        })

        switchMode(dispatch, 'basic')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic])

  useEventListener(`applyAnswerValue`, event => {
    if (disabled || null === parserResultPatternBasic) return

    const filter = filters
      .filter(group => ['questions', 'excluded'].includes(group.key))
      .flatMap(group => group.values)
      .find(opt => opt.key == event.detail.key)
    if (filter == undefined) return

    if (dropdownsRef.current[event.detail.key] && dropdownsRef.current[event.detail.key].current) {
      scrollCallback(event.detail.key)
    } else {
      dropdownCallback.current = event.detail.key
    }

    const values = convertTreeToValues(filter, state.basic[event.detail.key], 'value')
    const index = values.findIndex(v =>
      'multiselect' == filter.type ? v == event.detail.value : v.value == event.detail.value
    )

    switchMode(dispatch, 'basic')

    if (0 > index) {
      const option = 'multiselect' == filter.type ? event.detail.value : { state: true, value: event.detail.value }
      onChangeBasicFilter(filter, [...values, option])
    } else {
      const _values = [...values]
      if ('multiselect' == filter.type) _values.splice(index, 1)
      else {
        if (true == _values[index].state) _values[index].state = false
        else _values.splice(index, 1)
      }
      onChangeBasicFilter(filter, _values)
    }
  })

  useEventListener('applyUserFilter', event => {
    if (disabled || features.advanced.disabled) return

    const { pattern, filter } = event.detail

    const currentAdvPart = state.advanced.trim()
    const regexp = new RegExp(`\\(\\(\\(${pattern}\\)\\)\\)`, 'i')

    let newFilter = ''
    if (!parserResultPatternError && '' != currentAdvPart) {
      newFilter = regexp.test(currentAdvPart)
        ? currentAdvPart.replace(regexp, filter)
        : `${currentAdvPart} ${
            currentAdvPart.endsWith(' and') || currentAdvPart.endsWith(' or') ? '' : 'and'
          } ${filter}`
    } else {
      newFilter = filter
    }

    filtersChangingRef.current = true

    switchMode(dispatch, 'advanced')
    setAdvancedValue(dispatch, newFilter)
  })

  useEventListener('customQuestionRemoved', event => {
    if (null === parserResultPatternBasic) return

    deleteBasicValue(dispatch, event.detail)
  })

  useEventListener('customQuestionChanged', event => {
    if (null === parserResultPatternBasic) return

    if (Object.hasOwn(event.detail, 'variant')) {
      if (filtersApplying) return

      filtersChangingRef.current = true

      setBasicValue(dispatch, null)

      return
    }

    const treeValue = {}

    if (filtersUIDs[event.detail.id]) {
      const basic = state.basic[event.detail.id]
      if (basic == undefined) return

      const treeKeys = Object.keys(basic)

      for (const treeKey of treeKeys) {
        const newKey = event.detail.items[filtersUIDs[event.detail.id][treeKey]]
        if (newKey == undefined) continue

        treeValue[newKey] = basic[treeKey]
      }

      if (0 < Object.keys(treeValue).length) {
        if (event.detail.delay) {
          questionsCallback.current = () => changeBasicValue(dispatch, { [event.detail.id]: treeValue })
        } else {
          changeBasicValue(dispatch, { [event.detail.id]: treeValue })
        }
      } else deleteBasicValue(dispatch, event.detail.id)
    } else {
      const items = Object.values(event.detail.items)

      const treeKeys = Object.keys(filtersUIDs).filter(
        key => null !== key.match(new RegExp(`^${event.detail.id}(?!\\d)`, 'gi'))
      )

      if (0 < treeKeys.length) {
        for (const key of treeKeys) {
          const basic = state.basic[key]
          if (basic == undefined) continue

          treeValue[key] ||= null

          const item = event.detail.items[filtersUIDs[key]]
          if (item == undefined) continue

          const newKeys = Object.keys(basic).filter(v => Number(v.slice(1)) <= items[0].size)

          if (0 < newKeys.length)
            treeValue[`${event.detail.id}${item.key}`] = newKeys.reduce((acc, cur) => {
              acc[cur] = basic[cur]
              return acc
            }, {})
          else treeValue[`${event.detail.id}${item.key}`] = null
        }

        if (event.detail.delay) {
          questionsCallback.current = () => changeBasicValue(dispatch, treeValue)
        } else {
          changeBasicValue(dispatch, treeValue)
        }
      }
    }
  })

  const dataContextValue = useMemo(() => {
    return {
      root,
      features,
      state,
      dispatch,
      windowUID: data.windowUID,
      externalData,
      disabled,
      dropdownsRef,
      restoreQuestion,
      getApiKey,
      onScrollToQuestion,
      onChangeAdvancedFilter,
      onChangeBasicFilter,
      onResetBasicFilter,
      onShowFilterDropdown,
      onHideFilterDropdown,
      onShowHiddenGroup,
      onDNDStart,
      onDNDEnd
    }
  }, [
    root,
    features,
    state,
    dispatch,
    data.windowUID,
    externalData,
    disabled,
    dropdownsRef,
    restoreQuestion,
    getApiKey,
    onScrollToQuestion,
    onChangeAdvancedFilter,
    onChangeBasicFilter,
    onResetBasicFilter,
    onShowFilterDropdown,
    onHideFilterDropdown,
    onShowHiddenGroup,
    onDNDStart,
    onDNDEnd
  ])

  return (
    <div
      ref={componentRef}
      className={`${style.component}${
        disabled || ('advanced' == state.mode && features.advanced.disabled) ? ' ' + style['component-disabled'] : ''
      }`}
    >
      <div
        className={`${style.header}${
          disabled || ('advanced' == state.mode && features.advanced.disabled) ? ' ' + style['header-disabled'] : ''
        } p-fluid`}
      >
        {'advanced' == state.mode || !features.advanced.disabled ? (
          'advanced' == state.mode && null === parserResultPatternBasic ? (
            <div className={`${style.toggle} ${style['toggle-disabled']} p-field p-col-12`}>
              Simplify/clear logic to switch to the basic mode
            </div>
          ) : (
            <div
              className={`${style.toggle} p-field p-col-12`}
              onClick={() => switchMode(dispatch, 'advanced' == state.mode ? 'basic' : 'advanced')}
            >
              {'basic' == state.mode && 0 < state.advanced.length && !features.advanced.hidden ? (
                <TextPopup
                  text={`switch to ${'advanced' == state.mode ? 'basic mode' : 'advanced mode'}`}
                  content={truncate(state.advanced, { length: 500, separator: ' ' })}
                  direction="bottomleft"
                  portal
                />
              ) : 'basic' == state.mode && features.advanced.hidden ? null : (
                <>switch to {'advanced' == state.mode ? 'basic mode' : 'advanced mode'}</>
              )}
            </div>
          )
        ) : null}
        {disabled ? null : (
          <div className={`${style.apply} p-field p-col-12`}>
            {filtersChanged && !filtersReleasing ? (
              <Button
                className="p-button-stat p-button-rounded"
                label={`Click here to ${!state.advanced.length ? 'clear' : 'apply'} filters`}
                disabled={filtersApplying || parserResultPatternError}
                onClick={filtersApplying || parserResultPatternError ? null : onApplyFilters}
              />
            ) : 0 < filtersPattern.length ? (
              <Button
                className="p-button-stat p-button-rounded"
                label={
                  'basic' == state.mode && null !== parserResultPatternBasic
                    ? `Release ${filtersCountApplied} active ${plural(filtersCountApplied, 'filter', 'filters')}`
                    : 'Release active filters'
                }
                disabled={filtersApplying || filtersReleasing}
                onClick={filtersApplying || filtersReleasing ? null : onReleaseActiveFilters}
              />
            ) : null}
          </div>
        )}
      </div>
      <DataContext.Provider value={dataContextValue}>
        {('advanced' == state.mode || null === parserResultPatternBasic) && (
          <FiltersAdvanced
            parser={parser}
            locale={locale}
            surveySchema={surveySchema}
            clusters={clusters}
            parserResultEof={parserResultEof}
          />
        )}
        {('advanced' == state.mode || null === parserResultPatternBasic) &&
          isAdmin &&
          !disabled &&
          0 < filtersPattern.length && <FiltersUids key={filtersPattern} uids={uids} uidsTruncated={uidsTruncated} />}
        {filters
          .filter(
            filter =>
              ('advanced' == state.mode || null === parserResultPatternBasic ? filter.advanced : filter.basic) != false
          )
          .map(filter => (
            <FiltersGroup key={filter.key} group={filter} />
          ))}
      </DataContext.Provider>
    </div>
  )
}

FiltersPanel.propTypes = {
  root: PropTypes.any,
  data: PropTypes.object,
  features: PropTypes.object,
  parser: PropTypes.any.isRequired,
  locale: PropTypes.instanceOf(Map),
  filtersMode: PropTypes.string,
  filtersPattern: PropTypes.string,
  externalData: PropTypes.array,
  filters: PropTypes.array,
  tags: PropTypes.object,
  codeBooks: PropTypes.array,
  clusters: PropTypes.object,
  surveySchema: PropTypes.object,
  isAdmin: PropTypes.bool,
  uids: PropTypes.array,
  uidsTruncated: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onScrollToQuestion: PropTypes.func,
  onSwitchMode: PropTypes.func,
  onShowHiddenGroup: PropTypes.func,
  onLoad: PropTypes.func,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func,
  restoreQuestion: PropTypes.func,
  getApiKey: PropTypes.func
}

FiltersPanel.displayName = 'FiltersPanel'

export default FiltersPanel
